import React, { useState, useCallback, DragEvent } from "react";
import { useDispatch } from "react-redux";
import ReactFlow, {
  NodeChange,
  EdgeChange,
  Connection,
  HandleType,
  ReactFlowInstance,
} from "reactflow";
import { blockConfigs } from "../../models/block";
import { addBlock } from "../../store/game/gameSlice";
import { useAppSelector } from "../../hooks";
import {
  nodesChanged,
  edgesChanged,
  onConnect,
  nodeHandleClick,
  connectEnd,
} from "../../store/game/gameSlice";
import { selectNodes, selectEdges } from "../../store/game/selectors";
import ReactFlowBlock from "../block/ReactFlowBlock";
import ReactFlowEdge from "../block/ReactFlowEdge";

const nodeTypes = { gameBlock: ReactFlowBlock };
const edgeTypes = { gameEdge: ReactFlowEdge };

const BlockCanvas: React.FC = () => {
  const dispatch = useDispatch();
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);

  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');

      // check if the dropped element is valid
      if (typeof type === 'undefined' || !type) {
        return;
      }

      const position = reactFlowInstance?.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      if (!position) return;

      const blockConfig = blockConfigs[type as keyof typeof blockConfigs];
      if (!blockConfig) return;

      const { inputCapacity, processingCapacity } = blockConfig;

      dispatch(
        addBlock({
          top: position.y,
          left: position.x,
          id: "temp",
          width: 100,
          height: 100,
          blockType: type as any,
          inputCapacity,
          processingCapacity,
        })
      );
    },
    [reactFlowInstance, dispatch]
  );

  const nodes = useAppSelector((state) => selectNodes(state.game));
  const edges = useAppSelector((state) => selectEdges(state.game));

  const handleNodesChanged = (changes: NodeChange[]) => {
    dispatch(nodesChanged(changes));
  };

  const handleEdgesChanged = (changes: EdgeChange[]) => {
    dispatch(edgesChanged(changes));
  };

  const handleConnect = (connection: Connection) => {
    dispatch(onConnect(connection));
  };

  const handleConnectStart = (
    event: any,
    params: {
      nodeId: string | null;
      handleId: string | null;
      handleType: HandleType | null;
    }
  ) => {
    dispatch(
      nodeHandleClick({
        nodeID: params.nodeId || "",
        type: params.handleType || "",
      })
    );
  };

  const handleConnectEnd = () => {
    dispatch(connectEnd());
  };
  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={handleNodesChanged}
      onEdgesChange={handleEdgesChanged}
      onConnect={handleConnect}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onConnectStart={handleConnectStart}
      onConnectEnd={handleConnectEnd}
      onInit={setReactFlowInstance}
      onDrop={onDrop}
      onDragOver={onDragOver}
      deleteKeyCode={""}
    />
  );
};

export default BlockCanvas;
